import React, {useEffect, useRef, useState, act} from "react";
import {DataSet, Network, networkOptions} from "vis-network/standalone";
import "./visnetwork.css"
import GenSVG from "./glycan-interpreter/genSVG";
import Draggable from "react-draggable";
import AutoSVG from "./automatic-svg/AutoSVG";
import SNFGsvg from "./glycan-interpreter/snfgSVG";
import {Accordion,ModalHeader,ModalContent,ModalActions,Button,Header,Image,Modal, AccordionTitle, AccordionContent, IconDropdownMenu, Input, DropdownItem,DropdownDivider,Dropdown, DropdownMenu, ListItem, ListIcon,ListHeader,ListDescription,ListContent,List} from 'semantic-ui-react';
import {parseString, genSVG} from './glycan-interpreter/parser';
import generateTreeLayout from "./glycan-interpreter/treeGen";
import resmap from './data/resdict.json';
import restypemap from './data/restypedict.json';
import ContextMenu from "./components/ContextMenu";
import * as VisUtils from './libs/visutils';
import * as WindowUtils from './libs/windowutils';
import * as FileUtils from './libs/fileutils';
import * as IndexDBUtils from './libs/indexdbutils';
import EditEdge from "./components/EditEdge";
import { Tooltip as ReactTooltip} from 'react-tooltip';
import Joyride, {ACTIONS, EVENTS, STATUS} from 'react-joyride';
import {ToastContainer, toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function VisNetwork({dbAntigenStructure})
{
  //Network state
  const [nodes] = useState(new DataSet([]));
  const [edges] = useState(new DataSet([]));
  const [network, setNetwork] = useState(null);
  const visJSRef = useRef(null);
  const parentRef = useRef(null);
  const [editEdgeSelection, setEditEdgeSelection] = useState(null);

  //File management state
  const [activeProject, setActiveProject] = useState(localStorage.getItem('glycan-session'));
  const [projOriginalName, setProjOriginalName] = useState("");
  const [recentProjects, setRecentProjects] = useState("");
  const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
  const [chooseNameOpen, setChooseNameOpen] = useState(false);
  const [sessionNameChoice, setSessionNameChoice] = useState("");
  const [exportFilename, setExportFilename] = useState("");
  const [renameProject, setRenameProject] = useState("");

  //Image managment state
  const [downloadOption, setDownloadOption] = useState("svg");
  const [pngPreview, setPNGPreview] = useState("");
  const svgRef = useRef(null);

  //Window management state
  const [structFilter, setStructFilter] = useState("");
  const [structList, setStructList] = useState([]);
  const [addChainOpen, setAddChainOpen] = useState(false);
  const [modalInput, setModalInput] = useState("");
  const [exportOpen, setExportOpen] = useState(false); 
  const [fileDropDownOpen, setFileDropDownOpen] = useState(false);
  const [editDropDownOpen, setEditDropDownOpen] = useState(false);
  const [viewDropDownOpen, setViewDropDownOpen] = useState(false);
  const [addDropDownOpen, setAddDropDownOpen] = useState(false);
  const [projectsListOpen, setProjectsListOpen] = useState(false);
  
  //Editor management state
  const [addEdgeOn, setAddEdgeOn] = useState(true);
  const [addOffset, setAddOffset] = useState({x:0, y:0});
  const [gridDensity, setGridDensity] = useState(50);
  const [pageState, setPageState] = useState({gridlines:true, glossary:false, scale:100, gridsnap:false, structures:false, keyboardshortcuts:false})
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  const projNameRef = useRef(null);
  const [contextMenu, setContextMenu] = useState({position:{x:0, y:0}, offset:{x:0, y:0}, toggled:false, buttons:[]});
  const [editEdgeMenu, setEditEdgeMenu] = useState({position:{x:0, y:0}, offset:{x:0, y:0}, toggled:false});

  //Residue search management state
  const [residueSearch, setResidueSearch] = useState("");
  const Residues = Object.keys(resmap).map((residue) => ({
    key: residue,
    text: residue,
    value: residue
  }));
  
  const [reactJoyrideRun, setReactJoyrideRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const [projectLoaded, setProjectLoaded] = useState(false);

  const data = {
    nodes: nodes,
    edges: edges
  };
  const options = {
  interaction:{
    dragNodes:true,
    dragView: true,
    hideEdgesOnDrag: false,
    hideEdgesOnZoom: false,
    hideNodesOnDrag: false,
    hover: false,
    hoverConnectedEdges: false,
    keyboard: {
      enabled: true,
      speed: {x: 10, y: 10, zoom: 0.02},
      bindToWindow: false,
      autoFocus: false,
    },
    multiselect: false,
    navigationButtons: false,
    selectable: true,
    selectConnectedEdges: true,
    tooltipDelay: 300,
    zoomSpeed: 1,
    zoomView: false
  },
  edges:{
    color:"#000000",
    width:5,
    font: {
      vadjust:-10,
      align:"top"
    },
    smooth:false
  },
  physics:{
    enabled: false
  },
  manipulation:{
    enabled:false,
    addEdge: function (data, callback) {
      callback(data);
      pushPageState();
      checkAddEdge();
    },
    editEdge: {
      editWithoutDrag: function(data, callback) {
        editEdgeWithoutDrag(data, callback)
      }}
  }
  };

  useEffect(() => {
    console.log('loaded project');
    if(activeProject){
      loadProject(activeProject);
    } else {
      setProjectLoaded(true);
    }
    setAddEdgeOn(false);

    const trySave = () => {
      if (projectLoaded){
        save();
      }
    }
    return trySave;
  }, [projectLoaded])

  useEffect(() => {
    //draw nodes and edges network
    if (visJSRef.current) {
      setNetwork(new Network(visJSRef.current, data, options));
    }
    updateProjectList();
  }, [visJSRef, nodes, edges]);

  //--------------------------- Network Functions -------------------------------//
  //Toggles add edge option.
  function checkAddEdge()
  {
    if(addEdgeOn){
      setAddEdgeOn(!addEdgeOn);
    }
  }

  //Allows users to add edges using mouse drag.
  function editEdgeWithoutDrag(data, callback)
  {
    setEditEdgeMenu({...editEdgeMenu, toggled:true});
    setContextMenu({...contextMenu, toggled:false});
    setEditEdgeSelection(data.id);
  }

  //Updates edge with new label.
  function updateEdge(newLabel)
  {
    edges.update({id:editEdgeSelection, label:newLabel});
    pushPageState();
  }

  //--------------------------- Window Functions -------------------------------//

  const filteredResidues = Residues.filter((residue) => {
    return residue.text.toLowerCase().includes(residueSearch.toLowerCase());
  });

  //reset residue search when glossary is closed
  useEffect(() => {
    if(!pageState.glossary){
      setResidueSearch("");
    }
  }, [pageState.glossary])
  
  //Adds residue to page on click.
  function addResidue(resname)
  {
    const newNode = WindowUtils.clickToAddSymbol(resname, nodes.get(), addOffset);
    nodes.add(newNode);
    network.storePositions();
    pushPageState();
  }

  //Toggles window active/inactive.
  function toggleWindow(windowName)
  {
    let curState = {...pageState};
    curState[windowName] = !curState[windowName];
    setPageState(curState);
  }

  //Closes window.
  function closeWindow(windowName)
  {
    let curState = {...pageState};
    curState[windowName] = false;
    setPageState(curState);
  }

  const panels = Object.keys(restypemap).map(type => {
    return {key: type, title: type, content: {
      content: ( 
      <div className="network panels">
        {restypemap[type].map(residue => {
          let label = residue;

          if(residue == type){
            label = "Generic"
          }
          return(
            <div className="network panelcontent">
              <div className="network panelcontent svg" viewbox="0 0 30 30" key={residue} onClick={() => addResidue(residue)} dangerouslySetInnerHTML={{ __html: genSVG(resmap[residue].shape, resmap[residue].colour,false, false, 75, "white").svg }} />
              <div className="network paneltext">{label}</div>
            </div>
        )}
      )}
      </div>)
    }}
  });

//--------------------------- Page Management Functions -------------------------------//

  //Saves page state to undo stack.
  function pushPageState()
  {
    setUndoStack(prevStack => {
      const newState = {nodes: [...nodes.get()], edges: [...edges.get()]};
      const newUndoStack = [...prevStack, newState].slice(-6);
      return newUndoStack;
    });

    setRedoStack([]);
  }

  //Revert to previous page state.
  function undo()
  {
    let unStack = [...undoStack];
    let reStack = [...redoStack];

    if(unStack.length > 1){
      const nowState = unStack.pop();
      reStack.push(nowState);
      setRedoStack(reStack);
      const preState = unStack[unStack.length-1];
      setUndoStack(unStack);
      nodes.clear();
      nodes.add(preState.nodes);
      edges.clear();
      edges.add(preState.edges);
    }
  }

  //Redo page state forward.
  function redo()
  {
    let unStack = [...undoStack];
    let reStack = [...redoStack];

    if(reStack.length > 0){
      const nowState = reStack.pop();
      unStack.push(nowState);
      setUndoStack(unStack);
      setRedoStack(reStack);
      nodes.clear();
      nodes.add(nowState.nodes);
      edges.clear();
      edges.add(nowState.edges);
    }
  }

  //Event on project name change.
  function onSessionNameChoiceChange(event)
  {
    setSessionNameChoice(event.target.value);
  }

  //Event on structure filter view change.
  function onStrucFilterChange(event)
  {
    setStructFilter(event.target.value);
  }

  //Handles keypresses.
  function handleKeyDown(event)
  {
    if(event.key === 'Delete' || event.key === 'Backspace'){
      deleteSelected();
    } else if (event.key === 'c' && event.ctrlKey){
      event.preventDefault();
      copy()
    } else if (event.key === 'v' && event.ctrlKey){
      event.preventDefault();
      paste()
    } else if (event.key === 'g' && event.ctrlKey){
      event.preventDefault();
      toggleWindow("gridlines")
    } else if (event.key === 's' && event.ctrlKey){
      event.preventDefault();
      save()
    } else if (event.key === 'z' && event.ctrlKey){
      event.preventDefault();
      undo()
    } else if (event.key === 'y' && event.ctrlKey){
      event.preventDefault();
      redo()
    }
  }

  //Handles when other projects are selected.
  function handleChangeProjectFocus()
  {
    setProjOriginalName(projNameRef.current.innerText);
    setRenameProject(projNameRef.current.innerText);
  }

  //Handles project name change event.
  function handleChangeProjectName(event)
  {
    let cleanedInput = event.target.innerText.replace(/\n/g, '');
    if(cleanedInput === '') {cleanedInput = "Untitled"}
    setRenameProject(cleanedInput);
  }

  //Handles project set name event.
  function handleSetProjectName(event)
  {
    if (event.type === 'blur' && renameProject !== projOriginalName) {
      try{
        overwriteProject(renameProject,projOriginalName);
      } catch (error){
        projNameRef.current.innerText = projOriginalName;
      }
      setRenameProject("");
    }
  }

  //Prevents enter key from being pressed for project name.
  function handleChangeProjectKey(event)
  {
    if (event.key === "Enter"){
      event.preventDefault();
      event.target.blur();
    }
  }

//--------------------------- Editor Management Functions -------------------------------//

  //Copy selection
  function copy()
  {
    if(network){
      const selection = network.getSelection();
      const clipNodes = selection.nodes.map(nodeID => nodes.get(nodeID));
      const clipEdges = selection.edges.map(edgeID => edges.get(edgeID));
      setClipboard({nodes:clipNodes, edges:clipEdges});
    }
  }

  //Paste selection
  function paste(position = null)
  {
    if(network && clipboard){
      let clipNodes = clipboard.nodes;
      let clipEdges = clipboard.edges;
      const nodesList = nodes.get().map(node => node.id)
      const idOffset = nodesList.length > 0 ? Math.max(...nodesList) + 2 : 0;

      const offset = position === null ? addOffset : position;

      clipNodes = clipNodes.map(node => {
        return {...node, id: node.id + idOffset, x:node.x + addOffset.x, y:node.y + addOffset.y};
      })
      clipEdges = clipEdges.map(edge => {
        return {label:edge.label, sidelnk:edge.sidelnk, from: edge.from + idOffset, to: edge.to + idOffset, dashes:edge.dashes};
      })
      nodes.add(clipNodes);
      edges.add(clipEdges);
      pushPageState();
    }
  }

  //Opens edit edge menu for selected edge.
  function selectEditEdge()
  {
    setContextMenu(contextMenu => ({...contextMenu, 
      toggled:false,
    }));
    setEditEdgeMenu(editEdgeMenu => ({...editEdgeMenu,
      position:contextMenu.position, 
      toggled:true,
    }));
  }

  //Closes edit edge menu.
  function closeEditEdge()
  {
    setEditEdgeMenu(editEdgeMenu => ({...editEdgeMenu,
      position:contextMenu.position, 
      toggled:false,
    }));
  }

  //Adds link between residue
  function addLink()
  {
    if(!addEdgeOn){
      setAddEdgeOn(true);
      network.unselectAll();
      network.addEdgeMode();
    } else {
      setAddEdgeOn(false)
      network.disableEditMode();
    }
  }

  //Delete selection
  function deleteSelected()
  {
    if(network){
      const selection = network.getSelection();
      if(selection.nodes.length > 0 || selection.edges.length > 0){
        network.deleteSelected();
        network.setSelection({nodes:[], edges:[]});
        pushPageState();
      }
    }
  }

  //Snap nodes to grid arrangement.
  function snapNodes()
  {
    nodes.forEach(node => {
      node.x = Math.round(node.x/gridDensity)*gridDensity;
      node.y = Math.round(node.y/gridDensity)*gridDensity;
      nodes.update(node);
    });
    network.storePositions();
    pushPageState();
  }

  //Fits canvas to screen.
  function fitCanvas()
  {
    if(network){
      network.fit();
      const netScale = network.getScale();
      setPageState({...pageState, scale:netScale*100})
    }
  }

  //Handles zoom augmentation event (zoom-in/zoom-out)
  function handleZoomLevelChange(event)
  {
    VisUtils.setPageScale(parseInt(event.target.value, 10), network, pageState, setPageState);
    setPageState({...pageState, scale:parseInt(event.target.value, 10)});
  }

  //Changes zoom scale of canvas.
  function deltaScale(delta)
  {
    const scale = pageState.scale + delta;
    VisUtils.setPageScale(scale, network, pageState, setPageState);
  }

  //Saves page state when node is dragged.
  function handleDragEnd(event)
  {
    if (event.nodes.length > 0){
      network.storePositions();
      pushPageState();
    }
    setAddOffset(network.getViewPosition());
  }

  //Adds CASPER structure to canvas.
  function addChain()
  {
    structList.push(modalInput);
    let data = parseString(modalInput);
    setModalInput("");
    let [dataNodes,dataEdges,arranged] = data; 
    // rearrange the nodes with automatic layout
    if (!arranged){
      let posMappings = generateTreeLayout(data);
      dataNodes = dataNodes.map(node => {
          return {...node, x:posMappings.get(node.id).x, y:posMappings.get(node.id).y}; 
      })
    }
    const nodesList = nodes.get().map(node => node.id)
    const idOffset = nodesList.length > 0 ? Math.max(...nodesList) + 2 : 0;
    dataNodes = dataNodes.map(node => {
      return {...node, id:node.id + idOffset, x:node.x + addOffset.x, y:node.y + addOffset.y};
    });

    dataEdges = dataEdges.map(edge => {
      return {...edge, from:edge.from + idOffset, to:edge.to + idOffset};
    });

    nodes.add(dataNodes);
    edges.add(dataEdges);
    
    pushPageState();
  }

  //Handles modal input changes.
  function onModalChange(event)
  {
    setModalInput(event.target.value);
  }

  //Used to select CASPER structure from shift-click.
  function recursiveSelect(nodeList)
  {
    let nodes = [];
    // let edges = params.edges;
    
    let nodeStack = [...nodeList];
    let edgeStack = [];

    while(nodeStack.length > 0)
    {
      let node = nodeStack.pop();
      if (!nodes.includes(node)){
        nodes.push(node);
        let connectedEdges = network.getConnectedEdges(node);
        connectedEdges.forEach(edge => {
          let connectedNodes = network.getConnectedNodes(edge);
          nodeStack.push(...connectedNodes);
        });
      }
    }

    network.selectNodes(nodes);
  }

  //Adds structure from CASPER text.
  function addStructure(structureText)
  {
    let data = parseString(structureText);
    setModalInput("");
    let [dataNodes,dataEdges,arranged] = data; 
    // rearrange the nodes with automatic layout
    if (!arranged){
      let posMappings = generateTreeLayout(data);
      dataNodes = dataNodes.map(node => {
          return {...node, x:posMappings.get(node.id).x, y:posMappings.get(node.id).y}; 
      })
    }
    const nodesList = nodes.get().map(node => node.id)
    const idOffset = nodesList.length > 0 ? Math.max(...nodesList) + 2 : 0;
    dataNodes = dataNodes.map(node => {
      return {...node, id:node.id + idOffset, x:node.x + addOffset.x, y:node.y + addOffset.y};
    });

    dataEdges = dataEdges.map(edge => {
      return {...edge, from:edge.from + idOffset, to:edge.to + idOffset};
    });

    nodes.add(dataNodes);
    edges.add(dataEdges);
    pushPageState();
  }

  useEffect(() => {
    if(network){
      network.on('dragEnd', handleDragEnd)
      network.on('click', (params) => {
        if(params.event.srcEvent.shiftKey){
          if(params.nodes.length > 0){
            recursiveSelect(params.nodes);
          } 
        }
        setContextMenu({...contextMenu, toggled:false})
        setEditEdgeMenu({...editEdgeMenu, toggled:false})
        closeAllDropdowns()
      });
      network.on("oncontext", (params) => {
        params.event.preventDefault();
        var nodeID = network.getNodeAt(params.pointer.DOM);
        var edgeID = network.getEdgeAt(params.pointer.DOM);
        if(visJSRef.current){
          const networkElement = visJSRef.current.getBoundingClientRect();
          const halfwayX = networkElement.width/2;
          const halfwayY = networkElement.height/2;
          const isCursorOnLeftSide = params.pointer.DOM.x < halfwayX;
          const isCursorOnTop = params.pointer.DOM.y < halfwayY;
          
          if(nodeID)
          {
            network.selectNodes([nodeID]);
            const nodePosition = network.getPositions([nodeID])[nodeID];
            const domPosition = network.canvasToDOM({x:nodePosition.x, y:nodePosition.y});
            setContextMenu({...contextMenu, 
              position:domPosition, 
              offset:{x:networkElement.x, y:networkElement.y},
              buttons:[
                {name:"Paste", func:paste},
                {name:"Save", func:save},
                {name:"Save As", func:() => setChooseNameOpen(true)} 
              ],
              toggled:true,
              isLeft:isCursorOnLeftSide,
              isTop:isCursorOnTop
            });
            setEditEdgeMenu({...editEdgeMenu,
              position:domPosition,
              offset:{x:networkElement.x, y:networkElement.y},
              toggled:false,
            });
          } else if (edgeID) 
          {
            network.unselectAll();
            network.selectEdges([edgeID]);
            setEditEdgeSelection(edgeID);
            setContextMenu({...contextMenu, 
              position:params.pointer.DOM, 
              offset:{x:networkElement.x, y:networkElement.y},
              buttons:[
                {name:"Edit Edge", func:selectEditEdge},
                {name:"Paste", func:paste},
                {name:"Save", func:save},
                {name:"Save As", func:() => setChooseNameOpen(true)},
              ],
              toggled:false,
              isLeft:isCursorOnLeftSide,
              isTop:isCursorOnTop
            });
            setEditEdgeMenu({...editEdgeMenu, 
              position:params.pointer.DOM, 
              offset:{x:networkElement.x, y:networkElement.y},
              toggled:true,
            });
          } else {
            setContextMenu({...contextMenu, 
              position:params.pointer.DOM, 
              offset:{x:networkElement.x, y:networkElement.y},
              buttons:[
                {name:"Paste", func:paste},
                {name:"Save", func:save},
                {name:"Save As", func:() => setChooseNameOpen(true)},
              ],
              toggled:true,
              isLeft:isCursorOnLeftSide,
              isTop:isCursorOnTop
            });
            setEditEdgeMenu({...editEdgeMenu,
              position:params.pointer.DOM, 
              offset:{x:networkElement.x, y:networkElement.y},
              toggled:false,
            });
          }
        }
      })
    }
  }, [network])

  useEffect(() => {
    if(network){
      VisUtils.setPageScale(pageState.scale, network, pageState, setPageState);
      network.off('beforeDrawing');
      if (pageState.gridlines)
      {
        network.on("beforeDrawing", (ctx) => VisUtils.drawGridlines(ctx, gridDensity, network));
      } else {
        network.on("beforeDrawing", (ctx) => {
          ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)});
      }
      network.redraw()
    }
  }, [pageState.scale, pageState.gridlines, network, gridDensity]);
  

//--------------------------- Project File Management Functions -------------------------------//

  //Creates new canvas.
  function newPage()
  {
    nodes.clear();
    edges.clear();
    setUndoStack([]);
    setRedoStack([]);
    setChooseNameOpen(true);
  }
  
  //Handles project upload from .sga file.
  function uploadProject()
  {
    save(); //save the current project
    let fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = '.sga';
    document.body.appendChild(fileInput);
    fileInput.click();
    fileInput.addEventListener('change', (event) => {
      let selectedFile = event.target.files[0];
      let reader = new FileReader();
      reader.onload = (event) => {
        let fileContent = event.target.result;
        loadProjectFromFile(fileContent);
      }
      reader.readAsText(selectedFile);
      document.body.removeChild(fileInput);
      setChooseNameOpen(true);
    })
    fileInput.addEventListener('cancel', () => {
      document.body.removeChild(fileInput);
    })
  }

  //Loads project from IndexDB
  function loadProject(name)
  {
    IndexDBUtils.getProject(name)
      .then(content => {setCurrentProject(name); loadProjectFromFile(content, false); setProjectLoaded(true)});
  }

  //Loads project from .sga file.
  function loadProjectFromFile(fileContents, deserialize = true)
  {
    let JSONfileContent;
    pushPageState();
    if (deserialize){
      JSONfileContent = JSON.parse(fileContents);
    } else {
      JSONfileContent = fileContents;
    }
    
    nodes.clear();
    nodes.add(JSONfileContent.nodes);
    edges.clear();
    edges.add(JSONfileContent.edges);
    setStructList(JSONfileContent.structList);
    pushPageState();
  }

  //Overwrites project when name is changed.
  function overwriteProject(name, toDelete)
  {
    const projName = name;
    const projContent = {nodes: nodes.get(), edges:edges.get(), undo:undoStack, redo:redoStack, pageState:pageState, structList:structList};
    IndexDBUtils.addProject(projName, projContent)
      .then(() => {setCurrentProject(projName); IndexDBUtils.deleteProject(toDelete); updateProjectList();})
      .catch((error) => {projNameRef.current.innerText = projOriginalName;});
  }

  //Sets project name on IndexedDB
  function setCurrentProject(projName)
  {
    localStorage.setItem('glycan-session', projName);
    setActiveProject(projName);
    updateProjectList();
  }

  //Downloads project from IndexedDB
  function downloadCurrentProject()
  {
    const projName = localStorage.getItem('glycan-session')
    IndexDBUtils.getProject(projName)
    .then((content) => {
      // const JSONContent = JSON.parse(content);
      FileUtils.exportProject(content, projName);
    })
  }

  //Updates project list when new project added.
  function updateProjectList()
  {
    IndexDBUtils.getProjectList().then(projects => {
      setRecentProjects(projects.map(project => {
        return <DropdownItem onClick={() => loadProject(project.name)}>{project.name}</DropdownItem>
      }))
    });
  }

  //Saves project.
  function save()
  {
    if(activeProject === null)
    {
      setChooseNameOpen(true)
    } else {
      const projContent = {nodes: nodes.get(), edges:edges.get(), undo:undoStack, redo:redoStack, pageState:pageState, structList:structList};
      const projName = localStorage.getItem('glycan-session');
      IndexDBUtils.saveProject(projName, projContent)
        .then(() => {toast.success("Project Saved Successfully!", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          })})
        .catch(() => {setChooseNameOpen(true); toast.error("Error Saving Project.", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          })})
    }
  }

  //Saves copy of project.
  function saveAs()
  {
    const projName = sessionNameChoice;
    const projContent = {nodes: nodes.get(), edges:edges.get(), undo:undoStack, redo:redoStack, pageState:pageState, structList:structList};
    IndexDBUtils.addProject(projName, projContent)
      .then(() => {setCurrentProject(projName); updateProjectList(); toast.success("Project Saved Successfully!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        })})
      .catch((error) => {setChooseNameOpen(true); toast.error("Error. Project name already exists", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        })});
  }

  //Deletes currently active project.
  function deleteCurrentProject()
  {
    if(activeProject !== null){
      const projName = localStorage.getItem('glycan-session');
      IndexDBUtils.deleteProject(projName)
        .then(() => {
          updateProjectList();
          IndexDBUtils.getProjectList().then(projects => {
            if(projects.length > 0){
              const firstProjName = projects[0].name;
              setCurrentProject(firstProjName);
              loadProjectFromFile(projects[0].content, false);
            } else {
              // setChooseNameOpen(true);
              setActiveProject(null);
              setStructList([]);
            }
          })
        })
        .catch()
    }
    
  }

  //Closes all dropdowns.
  function closeAllDropdowns()
  {
    setFileDropDownOpen(false);
    setEditDropDownOpen(false);
    setViewDropDownOpen(false);
    setAddDropDownOpen(false);
    setProjectsListOpen(false);
  }

  //Opens only one dropdown menu and closes the rest.
  function openSingleDropdown(name)
  {
    switch (name){
      case "file":
        if(!fileDropDownOpen){
          setFileDropDownOpen(true);
          setEditDropDownOpen(false);
          setViewDropDownOpen(false);
          setAddDropDownOpen(false);
          setProjectsListOpen(false);
        } else {
          closeAllDropdowns();
        }
        break;
      case "edit":
        if(!editDropDownOpen){
          setFileDropDownOpen(false);
          setEditDropDownOpen(true);
          setViewDropDownOpen(false);
          setAddDropDownOpen(false);
          setProjectsListOpen(false);
        } else {
          closeAllDropdowns();
        }
        break;
      case "view":
        if(!viewDropDownOpen){
          setFileDropDownOpen(false);
          setEditDropDownOpen(false);
          setViewDropDownOpen(true);
          setAddDropDownOpen(false);
          setProjectsListOpen(false);
        } else {
          closeAllDropdowns();
        }
        break;
      case "add":
        if(!addDropDownOpen){
          setFileDropDownOpen(false);
          setEditDropDownOpen(false);
          setViewDropDownOpen(false);
          setAddDropDownOpen(true);
          setProjectsListOpen(false);
        } else {
          closeAllDropdowns();
        }
        break;
    }
  } 

  useEffect(()=>{
    const handleBeforeUnload = (event) => {
      const message = "Are you sure you want to leave?";
      pushPageState();
      save();
      event.returnValue = message;
      return message;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    if(dbAntigenStructure !== ""){
      setModalInput(dbAntigenStructure);
      setAddChainOpen(true);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [])

//--------------------------- Image Management Functions -------------------------------//

  const fileFormatOptions = [{key:'.svg', text:'.svg', value:'.svg'}, 
                            {key:'.png', text:'.png', value:'.png'}]

  //Gets png preview of canvas element.
  function pngPreviewOnOpen()
  {
    if(visJSRef.current){
      const canvas = visJSRef.current.getElementsByTagName('canvas');
      if(canvas.length > 0){
        const imgData = canvas[0].toDataURL("image/png");
        setPNGPreview(imgData);
      }
    }
  }

  //--------------------------- React Joyride Steps -------------------------------//
  //Starts ReactJoyrideTour
  function handleStartReactJoyrideTour(){
    setStepIndex(0);
    setReactJoyrideRun(true);
  }

  const steps = [
    {
      target: 'body',
      content: 
        <div>
          <h2>Welcome to SugarDB!</h2>
          <p>This is a guided tour of our Carbohydrate Visualiser tool. To exit at any point, click the SKIP button.</p>
          <p>Let's get started!</p>
        </div>,
      placement:'center'
    },
    {
      target: visJSRef.current,
      title:'Canvas',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you drag structures with your mouse to arrange them.</p>
        </div>,
    },
    {
      title: 'File Menu',
      content: (
        <div>
          <p>The file menu contains actions related to saving, loading, and exporting projects.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".file-dropdown",
      placement: 'bottom',
    },
    {
      target: '.new-button',
      title:'New Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you create new projects. Projects are self-contained canvases that stay on your computer for later.</p>
        </div>,
    },
    {
      target: '.save-button',
      title:'Save Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you save your projects. Projects remain on your browser for later use.</p>
        </div>,
    },
    {
      target: '.savecopy-button',
      title:'Save Copy of Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here can create a copy of your project and save it under a different name.</p>
        </div>,
    },
    {
      target: '.project-list-button',
      title:'Project List',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>This is a list of your saved projects.</p>
        </div>,
    },
    {
      target: '.project-list',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Selecting one of these options fron this dropdown will swap your active project.</p>
        </div>,
    },
    {
      target: '.upload-project-button',
      title:'Upload Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can upload a .SGA project file from your computer.</p>
        </div>,
    },
    {
      target: '.download-project-button',
      title:'Download Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can download a .SGA project file to your computer.</p>
        </div>,
    },
    {
      target: '.export-image-button',
      title:'Export Image',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can download a .PNG or .SVG image of your project.</p>
        </div>,
    },
    {
      target: '.delete-project-button',
      title:'Delete Project',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can delete your current active project.</p>
        </div>,
    },
    {
      title: 'Edit Menu',
      content: (
        <div>
          <p>The edit menu contains actions related to canvas manipulation.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".edit-menu",
      placement: 'bottom',
    },
    {
      target: '.copy-button',
      title:'Copy Canvas Selection',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can copy your selected residues on your canvas to your clipboard. This can also be done with <strong>CTRL+C</strong>.</p>
        </div>,
    },
    {
      target: '.paste-button',
      title:'Paste Clipboard',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can paste your clipboard selection to the canvas. This can also be done with <strong>CTRL+P</strong>.</p>
        </div>,
    },
    {
      target: '.delete-button',
      title:'Delete Canvas Selection',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can delete your current canvas selection. This can also be done with the <strong>DEL</strong> key.</p>
        </div>,
    },
    {
      title: 'View Menu',
      content: (
        <div>
          <p>The view menu contains viewing options.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".view-menu",
      placement: 'bottom',
    },
    {
      target: '.gridline-button',
      title:'Toggle Gridlines',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can toggle the canvas gridlines.</p>
        </div>,
    },
    {
      title: 'Add Menu',
      content: (
        <div>
          <p>The view menu contains options for adding structures to the canvas.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".add-menu",
      placement: 'bottom',
    },
    {
      target: '.new-structure-button',
      title:'New Structure',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can add a structure from a CASPER formatted string.</p>
        </div>,
    },
    {
      target: '.new-link-button',
      title:'New Link',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Here you can add a link by clicking on this button and dragging between two residues on the canvas.</p>
          <p>To edit the label on edges, <strong>right-click</strong> the edge with your mouse cursor.</p>
        </div>,
    },
    {
      target: '.new-residue-button',
      title:'New Residue',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Clicking on this button toggles the residue-list window. Here you can add an individual residue by clicking on one of the SNFG symbols in the window.</p>
        </div>,
    },
    {
      target: '.previously-added-structure-button',
      title:'Previously Added Structure',
      placement:'right',
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      content: 
        <div>
          <p>Clicking on this button toggles a list of structures added via the New Structure option. Clicking on a structure in this list will add it to the canvas.</p>
        </div>,
    },
    {
      title: 'Snap To Grid',
      content: (
        <div>
          <p>Click here to snap residues on the canvas to gridlines.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".snap-grid-icon",
      placement: 'bottom',
    },
    {
      title: 'Add New Structure',
      content: (
        <div>
          <p>Click here for a shortcut to add a new structure from a CASPER string.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".add-res-icon",
      placement: 'bottom',
    },
    {
      title: 'Add New Link',
      content: (
        <div>
          <p>Click here for a shortcut to add a new chemical bond.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".add-link-icon",
      placement: 'bottom',
    },
    {
      title: 'Project Name',
      content: (
        <div>
          <p>Click here to edit the name of the current project.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".project-name-text",
      placement: 'bottom',
    },
    {
      title: 'Undo',
      content: (
        <div>
          <p>Click here to undo your last action.</p>
          <p>You can also use <strong>CTRL+Z</strong>.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".undo-button",
      placement: 'bottom',
    },
    {
      title: 'Redo',
      content: (
        <div>
          <p>Click here to redo your last undo.</p>
          <p>You can also use <strong>CTRL+Y</strong>.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".redo-button",
      placement: 'bottom',
    },
    {
      title: 'Fit Layout',
      content: (
        <div>
          <p>Click here to fit all canvas elements onto the page.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".fit-canvas",
      placement: 'top',
    },
    {
      title: 'Zoom Dropdown',
      content: (
        <div>
          <p>Click here to select from pre-defined zoom levels.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".zoom-dropdown",
      placement: 'top',
    },
    {
      title: 'Zoom Out',
      content: (
        <div>
          <p>Click here to zoom-out the canvas.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".zoom-out",
      placement: 'top',
    },
    {
      title: 'Zoom In',
      content: (
        <div>
          <p>Click here to zoom-in the canvas.</p>
        </div>
      ),
      styles: {
        options: {
          zIndex: 10000,
        },
      },
      target: ".zoom-in",
      placement: 'top',
    },
    {
      target: 'body',
      content: 
        <div>
          <p>That's it for the tour! You can access this tour at any time by clicking the help-button at the top-right of the page.</p>
        </div>,
      placement:'center'
    },
  ];

  //Handles ReactJoyride states.
  function handleJoyrideCallback(data) {
    const { action, index, type, status } = data;

    if (action === ACTIONS.CLOSE || status === 'finished' || status === 'skipped') {
      setReactJoyrideRun(false);
    } else if (type == EVENTS.STEP_AFTER || type == EVENTS.TARGET_CLICK) {
      let nextIndex = index;
      if (action === ACTIONS.NEXT){
        setStepIndex(index + 1);
        nextIndex+=1;
      } else if (action === ACTIONS.PREV){
        setStepIndex(index - 1);
        nextIndex-=1;
      }
      switch (nextIndex){
        case 2:
          setFileDropDownOpen(false);
          break
        case 3:
          setFileDropDownOpen(true);
          break
        case 6:
          setProjectsListOpen(false);
          break
        case 7:
          setProjectsListOpen(true);
          break
        case 8:
          setProjectsListOpen(false);
          break
        case 11:
          setFileDropDownOpen(true);
          break
        case 12:
          setFileDropDownOpen(false);
          setEditDropDownOpen(false);
          break
        case 13:
          setEditDropDownOpen(true);
          break
        case 15:
          setEditDropDownOpen(true);
          break
        case 16:
          setEditDropDownOpen(false);
          setViewDropDownOpen(false);
          break
        case 17:
          setViewDropDownOpen(true);
          break
        case 18:
          setViewDropDownOpen(false);
          setAddDropDownOpen(false);
          break
        case 19:
          setAddDropDownOpen(true);
          break
        case 20:
          setAddDropDownOpen(true);
          break
        case 22:
          setAddDropDownOpen(true);
          break
        case 23:
          setAddDropDownOpen(false);
          break
      }
    }
  };

  //Continue ReactJoyride.
  function progressJoyride()
  {
    setStepIndex(stepIndex+1);
  }

  return (
    <>
    <Joyride steps={steps} stepIndex={stepIndex} run={reactJoyrideRun} continuous={true} scrollToFirstStep={true} showProgress={true} showSkipButton={true} callback={handleJoyrideCallback} styles={{
        buttonNext: {
          backgroundColor: '#6D9886',
          color: '#fff',
        },
        buttonBack: {
          color: '#6D9886',
        },
        buttonSkip:{
          display:"none"
        }
      }}/>
    <ToastContainer/>
    <div ref={parentRef}>
      <Modal className="modal panel" onClose={() => {setAddChainOpen(false); setModalInput("")}} onOpen={() => {setAddChainOpen(true); setModalInput("")}} open={addChainOpen}>
        <ModalHeader>Add Chain</ModalHeader>
        <ModalContent className="options addchain content">
          <Input  placeholder="CASPER String" value={modalInput} onChange={onModalChange} className="options addchain input"></Input>
          <div className="options addchain svg">
            <AutoSVG casperString={modalInput}/>
          </div>
        </ModalContent>        
        <ModalActions>
          <Button color="black" onClick={() => setAddChainOpen(false)}>Close</Button>
          <Button color="green" onClick={() => {addChain(); setAddChainOpen(false)}}>Add to canvas</Button>
        </ModalActions>
      </Modal>

      <Modal onClose={() => {setExportOpen(false)}} onOpen={() => {setExportOpen(true);}} open={exportOpen}>
        <ModalHeader>Export</ModalHeader>
        <ModalContent className="options addchain content">
        <Input labelPosition="right" label={<Dropdown defaultValue={downloadOption} options={fileFormatOptions} onChange={(event, {value}) => setDownloadOption(value)}/>} onChange={(event) => setExportFilename(event.target.value)} placeholder="Filename" className="options addchain input"></Input>
        <div className="exportpreview">
          {downloadOption === ".svg" ? 
            <div ref={svgRef} className="options addchain svg">
              <GenSVG offset={{x:100, y:50}} nodes={nodes} edges={edges}/>
            </div>
          :
            <img className="pngPreview" src={pngPreview}/>
          }
        </div>
        </ModalContent>        
        <ModalActions>
          <Button color="black" onClick={() => {setExportOpen(false); setExportFilename("")}}>Close</Button>
          <Button color="green" onClick={() => {downloadOption === '.svg' ? FileUtils.exportSVG(svgRef, exportFilename) : FileUtils.exportPNG(pngPreview, exportFilename); setExportOpen(false); setExportFilename("")}}>Export</Button>
        </ModalActions>
      </Modal>

      <ContextMenu position={contextMenu.position} offset={contextMenu.offset} isLeft={contextMenu.isLeft} isTop={contextMenu.isTop} buttons={contextMenu.buttons} isToggled={contextMenu.toggled} pasteOffset={contextMenu.networkRightClickPos}/>
      <EditEdge position={contextMenu.position} offset={editEdgeMenu.offset} isToggled={editEdgeMenu.toggled} submitEdge={updateEdge} closeDialog={closeEditEdge}/>

      <Modal onClose={() => {setChooseNameOpen(false); setSessionNameChoice("")}} onOpen={() => {setChooseNameOpen(true); sessionNameChoice("")}} open={chooseNameOpen} closeOnDimmerClick={false} closeOnEscape={false}>
        <ModalHeader>New Project</ModalHeader>
        <ModalContent className="options content">
          <Input placeholder="Project Name" onChange={onSessionNameChoiceChange} className="options addchain input"></Input>
        </ModalContent>        
        <ModalActions>
          <Button color="black" onClick={() => setChooseNameOpen(false)} >Close</Button>
          <Button color="green" onClick={() => {saveAs(); setChooseNameOpen(false)}}>Save</Button>
        </ModalActions>
      </Modal>

      <Modal className="editor modal" onClose={() => {setDeleteProjectOpen(false)}} onOpen={() => {setDeleteProjectOpen(false);}} open={deleteProjectOpen}>
        <ModalHeader>Delete Project</ModalHeader>
        <ModalContent className="options content">
          <p>Are you sure you want to delete: {activeProject}</p>
        </ModalContent>     
        <ModalActions>
          <Button color="black" onClick={() => setDeleteProjectOpen(false)}>Close</Button>
          <Button color="red" onClick={() => {deleteCurrentProject(); setDeleteProjectOpen(false)}}>Delete</Button>
        </ModalActions>
      </Modal>
      <div className="options topbar">
        <div className="options topbar left">
          <Dropdown text="File" className="options dropdown file-dropdown" inverted direction="right" onClick={() => openSingleDropdown("file")} open={fileDropDownOpen}>
            <DropdownMenu inverted onClick={progressJoyride}>
              <DropdownItem text='New' icon='file' data-tooltip-id="new" className='new-button' onClick={newPage}/>
              <DropdownItem text='Save' icon='save' data-tooltip-id="save" className='save-button' onClick={save}/>
              <DropdownItem text='Save a Copy' data-tooltip-id="save-copy" className='savecopy-button' icon='save outline' onClick={() => setChooseNameOpen(true)}/>
              <Dropdown text='Projects' pointing='left' data-tooltip-id="projects" className='link item project-list-button' onClick={() => setProjectsListOpen(!projectsListOpen)} open={projectsListOpen}>
                <DropdownMenu className="project-list">
                  {recentProjects}
                </DropdownMenu>
              </Dropdown>
              <DropdownItem text='Upload Project' data-tooltip-id="upload" className='upload-project-button' icon='folder' onClick={uploadProject}/>
              <DropdownItem text='Download Local Copy' data-tooltip-id="downloadproj" className='download-project-button' icon='download' onClick={downloadCurrentProject}/>
              <DropdownItem text='Export Image' icon='image' data-tooltip-id="exportimg" className='export-image-button' onClick={() => {setExportOpen(true); pngPreviewOnOpen()}}/>
              <DropdownItem text='Delete Project' icon='delete' data-tooltip-id="delete" className='delete-project-button' onClick={() => {if(activeProject) {setDeleteProjectOpen(true)} }}/>
            </DropdownMenu>
          </Dropdown>
          <Dropdown className="options dropdown edit-menu" text="Edit" invert onClick={() => openSingleDropdown("edit")} open={editDropDownOpen}>
            <DropdownMenu>
              <DropdownItem text='Copy' icon='copy' className="copy-button" data-tooltip-id="copy" onClick={copy}/>
              <DropdownItem text='Paste' icon='paste' className="paste-button" data-tooltip-id="paste" onClick={paste}/>
              <DropdownItem text='Delete' icon='delete' className="delete-button" data-tooltip-id="deletenodes" onClick={deleteSelected}/>
            </DropdownMenu>
          </Dropdown>
          <Dropdown className="options dropdown view-menu" text="View" closeOnEscape={false} onClick={() => openSingleDropdown("view")} open={viewDropDownOpen}>
            <DropdownMenu>
              <DropdownItem text='Gridlines' className="gridline-button" icon={pageState.gridlines ? "check" : ""} data-tooltip-id="gridlines" onClick={() => toggleWindow('gridlines')}/>
              <DropdownItem text='Keyboard Shortcuts Reference' className="gridline-button" icon={pageState.keyboardshortcuts ? "check" : ""} data-tooltip-id="keyboardshortcut" onClick={() => toggleWindow('keyboardshortcuts')}/>
            </DropdownMenu>
          </Dropdown>
          <Dropdown className="options dropdown add-menu" text="Add" closeOnEscape={false} onClick={() => openSingleDropdown("add")} open={addDropDownOpen}>
            <DropdownMenu>
              <DropdownItem text='New Structure' className="new-structure-button" icon='plus circle' data-tooltip-id="add-structure-list" onClick={() => setAddChainOpen(true)}/>
              <DropdownItem text='Chemical Link' className="new-link-button" icon='chain' data-tooltip-id="add-link-list" onClick={addLink}/>
              <DropdownItem text='Residue' className="new-residue-button" icon={pageState.glossary ? "check" : ""} data-tooltip-id="reslist" onClick={() => toggleWindow('glossary')}/>
              <DropdownItem text='Previously Added Structure' className='previously-added-structure-button' icon={pageState.structures ? "check" : ""} data-tooltip-id="struclist" onClick={() => toggleWindow('structures')} />
            </DropdownMenu>
          </Dropdown>
          <div className='options topbar iconbutton snap-grid-icon' data-tooltip-id="snap-grid" onClick={snapNodes}>
            <i  className="icon inverted magnet topbar"></i>
          </div>
          <div className='options topbar iconbutton add-res-icon' data-tooltip-id="add-structure" onClick={() => setAddChainOpen(true)}>
            <i  className="icon inverted plus circle topbar"></i>
          </div>
          <div className='options topbar iconbutton add-link-icon' data-tooltip-id="add-link" style={{background:addEdgeOn ? "#6D9886" : "#393E46"}} onClick={addLink}>
            <i  className="icon inverted chain topbar"></i>
          </div>
        </div>
        <p contentEditable data-tooltip-id="rename" onKeyDown={handleChangeProjectKey} onBlur={handleSetProjectName} onInput={handleChangeProjectName} onFocus={handleChangeProjectFocus} className="projectName project-name-text" ref={projNameRef}>{activeProject}</p>
        <div className="options topbar right">
          <div className='options topbar iconbutton undo-button' data-tooltip-id="undo" onClick={undo}>
            <i  className="icon inverted undo topbar"></i>
          </div>
          <div className='options topbar iconbutton redo-button' data-tooltip-id="redo" onClick={redo}>
            <i  className="icon inverted redo topbar"></i>
          </div>
          <div className='options topbar iconbutton' data-tooltip-id="help" onClick={handleStartReactJoyrideTour}>
            <i  className="icon inverted help circle topbar"></i>
          </div>
        </div>
      </div>

      { pageState.glossary ? (
        <Draggable handle=".handle" defaultPosition={{x:50, y:50}}>
          <div className="network draggable-window" style={{width:"30em"}}>
            <div className="network draggable-window-handle handle">
              Residue List
              <i onClick={() => closeWindow('glossary')} className="icon delete"></i>
            </div>
            <Input 
                style={{margin:'2vh'}} 
                fluid
                icon='search' 
                placeholder='Search...' 
                onChange={(e) => setResidueSearch(e.target.value)}
              />
            <div className="network draggable-window-content">
              { residueSearch === "" ? 
              <Accordion exclusive={false} panels={panels} fluid styled inverted/>
              :
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 0.3fr)', color:'white' }}>
                {filteredResidues.map((residue) => (
                  <div onClick={() => addResidue(residue.text)} key={residue.key} style={{ textAlign: 'center', marginBottom:'5vh'}}>
                    <SNFGsvg snfg={residue.text}  />
                    <strong >{residue.text}</strong>
                  </div>
                ))}
              </div>
              }
            </div>
          </div>
        </Draggable>
      ) : null}

      { pageState.structures ? (
        <Draggable handle=".handle" defaultPosition={{x:50, y:50}}>
          <div className="network draggable-window" style={{width:"30em"}}>
            <div className="network draggable-window-handle handle">
              Added Structures
              <i onClick={() => closeWindow('structures')} className="icon delete"></i>
            </div>
            <div className="network draggable-window-content">
              <List divided relaxed>
                <Input placeholder="Search..." onChange={onStrucFilterChange} className="options addchain input"></Input>
                {structList.filter(string => string.includes(structFilter)).map(string => 
                  <ListItem>
                    <div className="structures-item">
                      <div className="structures-thumbnail" onClick={() => addStructure(string)}>
                        <AutoSVG casperString={string}/>
                      </div>
                      <p className="structures-label">{string}</p>
                    </div>
                    
                  </ListItem>
                )}
              </List>
            </div>
          </div>
        </Draggable>
      ) : null}

      { pageState.keyboardshortcuts ? (
        <Draggable handle=".handle" defaultPosition={{x:50, y:50}}>
          <div className="network draggable-window" style={{width:"30em"}}>
            <div className="network draggable-window-handle handle">
              Keyboard Shortcut Reference
              <i onClick={() => closeWindow('structures')} className="icon delete"></i>
            </div>
            <div className="network draggable-window-content">
              <div className="keyboardshortcuts">
                <div><strong>CTRL+C</strong> : Copy selected structures.</div>
                <div><strong>CTRL+V</strong> : Paste from clipboard.</div>
                <div><strong>CTRL+Y</strong> : Redo</div>
                <div><strong>CTRL+Z</strong> : Undo</div>
                <div><strong>CTRL+S</strong> : Save Project</div>
                <div><strong>SHIFT+Right-Click</strong> : Select entire structure.</div>
                <div><strong>SHIFT+Drag</strong> : Selection window.</div>
                <div><strong>Right-Click (Page)</strong> : Open context menu.</div>
                <div><strong>Right-Click (Edge)</strong> : Edit edge label.</div>
              </div>
            </div>
          </div>
        </Draggable>
      ) : null}

      <div onKeyDown={handleKeyDown} className={`vis-network ${addEdgeOn ? "cross-cursor": ""}`} ref={visJSRef}/>
      <div className="options bottombar">
        <div className='options bottombar iconbutton fit-canvas' data-tooltip-id="fit-layout" onClick={fitCanvas}>
          <i  className="icon inverted expand bottombar icon"></i>
        </div>
        <Dropdown text="Zoom" data-tooltip-id="zoom-dropdown" className="options dropdown my-anchor-element zoom-dropdown" inverted direction="left">
          <DropdownMenu inverted>
            <DropdownItem text='25%' onClick={() => VisUtils.setPageScale(25, network, pageState, setPageState)}/>
            <DropdownItem text='50%' onClick={() => VisUtils.setPageScale(50, network, pageState, setPageState)}/>
            <DropdownItem text='75%' onClick={() => VisUtils.setPageScale(75, network, pageState, setPageState)}/>
            <DropdownItem text='100%' onClick={() => VisUtils.setPageScale(100, network, pageState, setPageState)}/>
            <DropdownItem text='125%' onClick={() => VisUtils.setPageScale(125, network, pageState, setPageState)}/>
          </DropdownMenu>
        </Dropdown>
        <div className='options bottombar iconbutton zoom-out' data-tooltip-id="zoomout" onClick={() => deltaScale(-5)} on>
          <i  className="icon inverted zoom-out bottombar icon"></i>
        </div>
        <input type="range" min={25} max={150} value={pageState.scale} onChange={handleZoomLevelChange} />
        <div className='options bottombar iconbutton zoom-in' data-tooltip-id="zoomin" onClick={() => deltaScale(5)}>
          <i  className="icon inverted zoom-in bottombar icon"></i>
        </div>
      </div>
    </div>
    
    <ReactTooltip id="undo" place="bottom" content="Undo" effect="solid" delayShow={100}/>
    <ReactTooltip id="redo" place="bottom" content="Redo" effect="solid" delayShow={100}/>
    <ReactTooltip id="help" place="bottom" content="Help" effect="solid" delayShow={100}/>
    <ReactTooltip id="add-link" place="bottom" content="Add Chemical Link" effect="solid" delayShow={100}/>
    <ReactTooltip id="add-structure" place="bottom" content="Add New Structure" effect="solid" delayShow={100}/>
    <ReactTooltip id="add-link-list" place="right" content="Add Chemical Link" effect="solid" delayShow={100}/>
    <ReactTooltip id="add-structure-list" place="right" content="Add New Structure" effect="solid" delayShow={100}/>
    <ReactTooltip id="snap-grid" place="top" content="Snap to Grid" effect="solid" delayShow={100}/>
    <ReactTooltip id="new" place="right" content="New Project" effect="solid" delayShow={100}/>
    <ReactTooltip id="save" place="right" content="Save Current Project" effect="solid" delayShow={100}/>
    <ReactTooltip id="save-copy" place="right" content="Save a Copy of Current Project" effect="solid" delayShow={100}/>
    <ReactTooltip id="projects" place="right" content="List of Active Projects" effect="solid" delayShow={100}/>
    <ReactTooltip id="upload" place="right" content="Upload Project from File (.sga)" effect="solid" delayShow={100}/>
    <ReactTooltip id="downloadproj" place="right" content="Download Project (.sga)" effect="solid" delayShow={100}/>
    <ReactTooltip id="exportimg" place="right" content="Export Project as Image (.png/.svg)" effect="solid" delayShow={100}/>
    <ReactTooltip id="delete" place="right" content="Delete Current Project" effect="solid" delayShow={100}/>
    <ReactTooltip id="copy" place="right" content="Copy Canvas Selection" effect="solid" delayShow={100}/>
    <ReactTooltip id="paste" place="right" content="Paste Clipboard" effect="solid" delayShow={100}/>
    <ReactTooltip id="deletenodes" place="right" content="Delete Selection" effect="solid" delayShow={100}/>
    <ReactTooltip id="gridlines" place="right" content="Toggle Gridlines" effect="solid" delayShow={100}/>
    <ReactTooltip id="reslist" place="right" content="Toggle Residue List Window" effect="solid" delayShow={100}/>
    <ReactTooltip id="struclist" place="right" content="Toggle Added Structures Window" effect="solid" delayShow={100}/>
    <ReactTooltip id="zoom-dropdown" place="top" content="Zoom Canvas" effect="solid" delayShow={100}/>
    <ReactTooltip id="fit-layout" place="top" content="Fit to Screen" effect="solid" delayShow={100}/>
    <ReactTooltip id="zoomin" place="top" content="Zoom In" effect="solid" delayShow={100}/>
    <ReactTooltip id="zoomout" place="top" content="Zoom Out" effect="solid" delayShow={100}/>
    <ReactTooltip id="rename" place="bottom" content="Click to Rename Project" effect="solid" delayShow={100}/>
    </>
  )
}

export default VisNetwork;